import { useRef, useState, useEffect } from 'react'

import { DateTime } from 'luxon'

import { getStatus } from '../constants/config'
import { useMissionClaimReward } from '../hooks/useMissionClaimReward'
import { useMissionSubmission } from '../hooks/useMissionSubmission'
import { useMissionsStore } from '../stores/useMissionsStore'

import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useMissionsAnalytics } from '@/shared/lib/analytics'
import { sanitizeRichText } from '@/shared/lib/sanitizeRichText'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/shared/ui/dialog'
import { AdminAutoCompleteButton } from '@/shared/ui/AdminAutoCompleteButton'
import { getIcon, StageText } from '@/shared/ui/Icons'
import { Modal } from '@/shared/ui/Modal'

import { MissionEvidenceForm } from './MissionEvidenceForm'
import { RegistrationNumberForm } from './RegistrationNumberForm'
import { SchoolDataForm } from './SchoolDataForm'
import { SubmissionStatusTag } from './SubmissionStatusTag'

interface MissionModalProps {
  missionId: number
}

export function MissionModal({ missionId }: MissionModalProps) {
  const buttonRef = useRef(null)

  const { refetchProgress } = useUserProgress()
  const { trackMissionStarted } = useMissionsAnalytics()
  const { claimReward } = useMissionClaimReward()

  const [isEvidenceFormOpen, setIsEvidenceFormOpen] = useState(false)
  const { mission, refetchMissionProgresses } = useMissionsStore({
    id: missionId
  })

  const { submission } = useMissionSubmission({ id: missionId })
  const isButtonDisabled =
    submission?.status === 'approved' || submission?.status === 'pending'
  const status = getStatus(mission)
  const Icon = getIcon(mission.icon) || StageText

  // useEffect(() => {
  //   if (mission) {
  //     trackMissionViewed(mission)
  //   }
  // }, [mission, trackMissionViewed])

  const getButtonText = () => {
    switch (submission?.status) {
      case 'approved':
        return 'Missão Concluída'
      case 'pending':
        return 'Aguardando Aprovação'
      case 'refused':
        return 'Tentar Novamente'
      default:
        return 'Iniciar Missão'
    }
  }

  const handleClaimReward = () => claimReward(mission, buttonRef.current)

  const isAnnualSchoolDataMission =
    mission?.slug === 'annual_school_data_update' ||
    (mission?.kind === 'progress' && mission?.recurrence_kind === 'yearly')

  const isRegistrationNumberMission =
    mission?.slug === 'registration_number_update'

  // Missões de progresso que exigem um formulário de dados de cadastro dentro
  // do próprio modal (dados escolares anuais e CPF lifetime).
  const isProfileFormMission =
    isAnnualSchoolDataMission || isRegistrationNumberMission

  if (!mission) return null
  if (
    submission?.status === 'approved' ||
    (mission?.kind === 'progress' &&
      (mission._progress?.completed || !isProfileFormMission))
  ) {
    return (
      <Modal className="sm:max-w-md">
        <div className="flex flex-col items-center gap-4">
          <img
            src="/misc/robot-jumping.png"
            className="block max-w-80 w-full mx-auto"
            alt="Recompensa"
          />
          <DialogHeader>
            <DialogTitle className="text-xl text-secondary font-sans uppercase text-center">
              Missão{' '}
              {mission._progress.completed || submission?.status === 'approved'
                ? 'Concluída'
                : ''}
            </DialogTitle>
            <DialogDescription className="text-dark leading-relaxed text-center text-lg font-bold">
              {mission.name}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gradient-to-b from-purple-100 to-purple-200 rounded-2xl p-4 w-full">
            <h3 className="text-lg text-center text-primary font-semibold mb-4">
              Recompensas
            </h3>

            <div className="flex items-center justify-center gap-6">
              {!!mission.point_reward && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold text-dark">
                    {mission.point_reward}
                  </span>
                  <img src="/icons/star.svg" className="size-10" alt="Pontos" />
                </div>
              )}

              {!!mission.coin_reward && (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold text-dark">
                    {mission.coin_reward}
                  </span>
                  <img src="/icons/coin.svg" className="size-10" alt="Moedas" />
                </div>
              )}
            </div>
          </div>
          {(mission._progress.completed ||
            submission?.status === 'approved') && (
            <Button
              ref={buttonRef}
              variant={
                mission?._progress?.claimed || submission?.status === 'approved'
                  ? 'primary'
                  : 'success'
              }
              className="h-[45px] w-full"
              onClick={() => {
                if (mission?._progress?.claimed) return
                handleClaimReward()
              }}
            >
              {mission?._progress?.claimed || submission?.status === 'approved'
                ? 'Recompensa já recebida'
                : 'Receber Recompensa'}
            </Button>
          )}
        </div>
      </Modal>
    )
  }

  return (
    <Modal>
      <div
        className={cn(
          'relative bg-gradient-to-b from-blue-300 to-blue-400 rounded-xl pt-8 bg-cover bg-no-repeat bg-position-[center_30%] pb-[40%]',
          {
            'bg-[url(/misc/robot-thumbs-up.jpg)]': mission.isGeneral
          }
        )}
      >
        {!!mission.imageUrl && (
          <img
            src={mission.imageUrl}
            alt={mission.name}
            className="absolute top-0 left-0 w-full h-full object-cover rounded-xl"
          />
        )}
        <div className="absolute top-3 left-3 size-15 flex items-center justify-center">
          <Icon className="size-20 object-contain" />
        </div>
      </div>

      <div className="space-y-4 bg-jacaranda/20 rounded-xl p-4">
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="text-xl text-secondary font-sans">
            {mission.name}
          </DialogTitle>
          <SubmissionStatusTag status={submission?.status} />
        </DialogHeader>

        {mission.description && (
          /*
           * asChild troca o <p> que o Radix renderiza por uma <div>: a
           * descrição vem do editor Rich Text e pode conter <p> e <ul>, que
           * não são HTML válido dentro de <p>. O asChild preserva o
           * aria-describedby que o Radix liga ao diálogo.
           *
           * Sem o utilitário `break-words`: ele fica em @layer utilities, que
           * vence @layer components na mesma especificidade, e sobrescreveria o
           * `overflow-wrap: anywhere` de .rich-text — justamente o que impede o
           * texto de esticar o modal. A quebra de linha é do .rich-text.
           */
          <DialogDescription asChild>
            <div
              className="rich-text text-dark leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(mission.description)
              }}
            />
          </DialogDescription>
        )}
      </div>

      <div className="bg-gradient-to-b from-purple-100 to-purple-200 rounded-2xl p-4">
        <h3 className="text-lg text-center text-primary font-semibold mb-4">
          Recompensas
        </h3>

        <div className="flex items-center justify-center gap-6">
          {!!mission.point_reward && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-semibold text-dark">
                {mission.point_reward}
              </span>
              <img src="/icons/star.svg" className="size-10" alt="Pontos" />
            </div>
          )}

          {!!mission.coin_reward && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-semibold text-dark">
                {mission.coin_reward}
              </span>
              <img src="/icons/coin.svg" className="size-10" alt="Moedas" />
            </div>
          )}
        </div>
      </div>
      {isProfileFormMission && status?.slug === 'open' && (
        <div className="space-y-4 rounded-xl p-4 bg-white/80">
          <h3 className="text-lg font-semibold text-primary">
            {isRegistrationNumberMission
              ? 'Informe seu CPF'
              : 'Atualize seus dados escolares'}
          </h3>
          {isRegistrationNumberMission ? (
            <RegistrationNumberForm
              mission={mission}
              onSuccess={() => {
                refetchMissionProgresses()
                refetchProgress()
              }}
            />
          ) : (
            <SchoolDataForm
              mission={mission}
              onSuccess={() => {
                refetchMissionProgresses()
                refetchProgress()
              }}
            />
          )}
        </div>
      )}
      {isEvidenceFormOpen && <MissionEvidenceForm mission={mission} />}
      {!isEvidenceFormOpen &&
        !(isProfileFormMission && status?.slug === 'open') && (
          <DialogFooter className="flex sm:flex-row md:justify-center w-full">
            {status?.slug == 'soon' && (
              <div className="flex flex-col">
                <span className="text-gray-500 d-block fs-8 text-end">
                  abre em
                </span>
                <span className="text-gray-600 fs-7 font-semibold d-block">
                  {DateTime.fromISO(mission?.start_at || '').toLocaleString(
                    DateTime.DATETIME_SHORT
                  )}
                </span>
              </div>
            )}
            {status?.slug == 'open' && (
              <div className="flex flex-col">
                <span className="text-gray-500 d-block fs-8 text-end">
                  encerra em
                </span>
                <span className="text-gray-600 font-semibold d-block">
                  {DateTime.fromISO(mission?.end_at || '').toLocaleString(
                    DateTime.DATETIME_SHORT
                  )}
                </span>
              </div>
            )}
            {status?.slug == 'closed' && (
              <span className="text-gray-600 d-block text-end font-semibold">
                encerrado
              </span>
            )}
            {status?.slug == 'open' && (
              <Button
                variant="primary"
                disabled={isButtonDisabled}
                onClick={() => {
                  if (isButtonDisabled) return

                  // Track quando missão é iniciada
                  trackMissionStarted(mission)
                  setIsEvidenceFormOpen(true)
                }}
              >
                {getButtonText()}
              </Button>
            )}
          </DialogFooter>
        )}

      {/* Missão destrava nível e regra de unlock; concluí-la pelo pipeline real
          é o que abre o conteúdo seguinte sem mexer na mecânica do aluno. */}
      {mission?.id ? (
        <AdminAutoCompleteButton
          type="Mission"
          id={mission.id}
          label="Auto-concluir missão"
          className="self-center"
        />
      ) : null}
    </Modal>
  )
}
